import { vec2 } from 'gl-matrix';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    centerBoundsAt,
    centeredBoundsFraction,
    computeGridAABB,
    registrationDefaultVoi,
} from '~/domain/services/imageService/gridGeometry';
import { PlaneView } from '~/engine/view/views/PlaneView';
import type { CanvasFrameGeometry } from '~/engine/view/TextureView';
import {
    CAMERA_REDRAW_EVENTS,
    usePlaneRedrawTick,
} from '~/hooks/viewer/annotationOverlay';
import { useRegistrationState } from '~/hooks/registration/useRegistrationStore';
import {
    VoiBounds,
    activeVoiConsumerState,
    voiBoundsState,
    voiShapeState,
} from '~/state/atoms/toolsAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import ProjectedVoiBoxWithHandles from './ProjectedVoiBoxWithHandles';
import {
    ellipsoidCrossSectionScale,
    isVoiBoxOnCurrentSlice,
    projectVoiBoundsToCanvasRect,
} from './projectVoiBounds';

/**
 * Registration's default VOI for what `view` shows: the whole primary image,
 * framed by `view` when it is zoomed in on part of it
 * ({@link registrationDefaultVoi}).
 */
function computeDefaultVoiBounds(view: PlaneView): VoiBounds | null {
    const primaryGrid = view.environment.image?.grid;
    if (!primaryGrid) {
        return null;
    }
    const visible = view.getPatientSpaceBoundingBox(0);
    const primary = computeGridAABB(primaryGrid);
    if (!visible.min || !visible.max) {
        return centeredBoundsFraction(primary, 1);
    }
    const rotation = view.rotation;
    const normal = [rotation[6], rotation[7], rotation[8]].map(Math.abs);
    return registrationDefaultVoi(primary, {
        visible: { worldMin: visible.min, worldMax: visible.max },
        normalAxis: normal.indexOf(Math.max(...normal)),
    });
}

interface VoiSelectorOverlayWrapperProps {
    view: PlaneView | null;
    frame?: CanvasFrameGeometry;
}

/**
 * Shared VOI selection box, rendered on every viewport whenever ANY
 * consumer is active (see {@link activeVoiConsumerState}) — not a
 * registration-only widget. The overlay owns projection and handle
 * editing of the one shared {@link voiBoundsState}; each consumer owns
 * its activation and the IMAGE its default is measured against.
 * Registration's default is seeded here because no registration panel
 * holds the bounds; the threshold-segmentation panel seeds its own before
 * activating.
 */
const VoiSelectorOverlay = ({
    view,
    frame,
}: VoiSelectorOverlayWrapperProps) => {
    const [voiBounds, setVoiBounds] = useRecoilState(voiBoundsState);
    const registrationState = useRegistrationState();
    const hasSelectedRegistration =
        registrationState.selectedSopInstanceUid !== undefined;
    const activeConsumer = useRecoilValue(activeVoiConsumerState);
    const regionShape = useRecoilValue(voiShapeState);
    const isOverlayActive = activeConsumer !== null;
    const viewManager = useRecoilValue(viewManagerState);

    // Emphasis pulse on threshold-segmentation activation ONLY: the box can be
    // restored from the session record on a panel reopen, where nothing
    // else on screen marks the moment it comes back. Registration is
    // deliberately excluded — its behavior is promised to be exactly as
    // before the overlay was shared. Purely presentational: bounds and
    // cache keys are untouched.
    const [activationPulseToken, setActivationPulseToken] = useState(0);
    const prevConsumerRef = useRef(activeConsumer);
    useEffect(() => {
        const previous = prevConsumerRef.current;
        prevConsumerRef.current = activeConsumer;
        if (
            activeConsumer === 'threshold-segmentation' &&
            previous !== 'threshold-segmentation'
        ) {
            setActivationPulseToken((token) => token + 1);
        }
    }, [activeConsumer]);

    // Re-render when the view matrix changes (pan, scroll, or zoom) so the
    // projected VOI box stays aligned with the underlying patient-space
    // region; over a frame, the frame brings those changes.
    usePlaneRedrawTick(frame ? null : viewManager, CAMERA_REDRAW_EVENTS);

    // A canvas resize also invalidates the projection, and only THIS
    // instance's: bump a local tick, never the shared voiBoundsState. That
    // atom is clinical state the panel persists into the region-restriction
    // session record, and only the drag handles, the double-click recenter
    // below and the consumers' seed/restore paths write it, so a drag in a
    // side viewport is identical to one in the main viewport.
    const [, setRenderTick] = useState(0);
    useEffect(() => {
        if (!view || !isOverlayActive) {
            return;
        }

        const canvasElement =
            (view as { canvasElement?: HTMLCanvasElement }).canvasElement ??
            (view as { canvas?: HTMLCanvasElement }).canvas;

        if (!canvasElement) {
            return;
        }

        const observer = new ResizeObserver(() => {
            setRenderTick((t) => t + 1);
        });

        observer.observe(canvasElement);
        return () => observer.disconnect();
    }, [view, isOverlayActive]);

    // The bounds the double-click handler below reads, held in a ref so
    // the listener is registered ONCE per canvas: keeping `voiBounds` in
    // the effect's dependency list tore down and re-attached the canvas
    // listener on every drag frame (each drag write is a new bounds
    // object), which is both needless churn and a window in which a
    // dblclick can land between removal and re-attachment.
    const voiBoundsRef = useRef(voiBounds);
    voiBoundsRef.current = voiBounds;

    // Double-click recenter AND navigate: translate the box (size
    // preserved) so its centre lands at the clicked point, and carry the
    // slice position there in the same gesture — placing the region and
    // moving to it were otherwise two separate actions on the same target — in-plane from the click, along
    // the view normal on the current slice plane (where
    // clientToPatientCoordinates places the click) — clamped into the
    // displayed image's AABB. Body-contour only. Consuming the click suppression keeps a
    // double-click straight after a pan/window drag from jumping the box.
    //
    // The listener sits on the CANVAS, and the box's own grab targets are
    // canvas siblings that cover it, so the box interior forwards dblclick
    // here (see ProjectedVoiBoxWithHandles). Without that forwarding a
    // double-click INSIDE the box, the most natural place to aim it,
    // would never reach the listener.
    useEffect(() => {
        if (!view || activeConsumer !== 'threshold-segmentation') {
            return;
        }
        const canvas = view.canvas;
        const grid = view.environment.image?.grid;
        if (!canvas || !grid) {
            return;
        }
        const onDoubleClick = (event: MouseEvent) => {
            const bounds = voiBoundsRef.current;
            if (!bounds) {
                return;
            }
            if (view.consumeClickSuppression()) {
                return;
            }
            const clickedPointMm = view.clientToPatientCoordinates(
                vec2.fromValues(event.clientX, event.clientY)
            );
            setVoiBounds(
                centerBoundsAt(bounds, clickedPointMm, computeGridAABB(grid))
            );
            // Carry the slice position to the same point, so one gesture
            // both frames the region and navigates to it. The CLICKED
            // point, not the recentred box's centre: the two differ when
            // the box is clamped at an image edge, and the user aimed at
            // the anatomy under the cursor.
            //
            // The plain crosshair move that scrolling uses, NOT
            // panZoom.focusViewsToPoint: that fits the views to a 30 mm box
            // around the point, so it re-zooms. Losing the zoom the user
            // had set is a bigger disruption than the navigation is worth.
            view.updatePlaneIntersectionPoint(clickedPointMm);
        };
        canvas.addEventListener('dblclick', onDoubleClick);
        return () => canvas.removeEventListener('dblclick', onDoubleClick);
    }, [view, activeConsumer, setVoiBounds]);

    const initializeVoiBounds = useCallback(() => {
        if (!view || !hasSelectedRegistration) {
            return;
        }

        // Keep existing bounds if already set (e.g. toggling off/on)
        if (voiBounds) {
            return;
        }

        // One view for every overlay of the Environment, so they seed one box.
        const defaultBounds = computeDefaultVoiBounds(
            viewManager?.lastUsedViewOf(view.environment) ?? view
        );
        if (defaultBounds) {
            setVoiBounds(defaultBounds);
        }
    }, [hasSelectedRegistration, voiBounds, setVoiBounds, view, viewManager]);

    // Registration-only seeding: other consumers seed their own default
    // BEFORE activating, and must never receive registration's box.
    useEffect(() => {
        if (activeConsumer === 'registration') {
            initializeVoiBounds();
        }
    }, [initializeVoiBounds, activeConsumer]);

    const computeProjectedVoiBox = () => {
        if (!voiBounds || !view) {
            return null;
        }

        // Slice-range visibility: the full interactive box renders only
        // while this view's current plane lies within the box's extent
        // along the view normal (± half a slice spacing — see
        // {@link isVoiBoxOnCurrentSlice}); elsewhere the component
        // renders the dimmed, dashed, non-interactive outline instead —
        // hidden entirely, a user scrolled away could no longer find the
        // restriction that is still in force. Scrolling re-evaluates
        // this through the frame its tile passes for the new slice, or
        // the planeIntersectionPointChanged re-render above without one,
        // and a drag from another viewport that moves the slab
        // off this slice re-evaluates through the bounds atom — while
        // the drag itself, whose window listeners live in the component,
        // is never interrupted mid-gesture. Every consumer of the shared
        // overlay (registration, threshold segmentation, SUV) gets the
        // same rule: a registration VOI drawn on every slice was equally
        // misleading about where the restriction lies.
        return (
            <ProjectedVoiBoxWithHandles
                rect={projectVoiBoundsToCanvasRect(view, voiBounds, frame)}
                voi={voiBounds}
                setVoi={setVoiBounds}
                view={view}
                activationPulseToken={activationPulseToken}
                interactive={isVoiBoxOnCurrentSlice(view, voiBounds, frame)}
                shape={regionShape}
                ellipsoidScale={ellipsoidCrossSectionScale(
                    view,
                    voiBounds,
                    frame
                )}
            />
        );
    };

    const projectedVoiBox = computeProjectedVoiBox();

    return isOverlayActive ? projectedVoiBox : null;
};

export default VoiSelectorOverlay;
